import { AllowedEvent } from '@fhs-legacy/frontend/src/state/crm-events/types';

import { PaymentOrchestrator, PaymentPipelineError, PaymentPipelineResult } from '../../../payment';
import { usePaymentFlowStore } from '../../../state/payment-stores/payment-flow-store';

interface HandlePlaceOrderDependencies {
  lastResultRef: React.MutableRefObject<PaymentPipelineResult | null>;
  orchestratorRef: React.MutableRefObject<PaymentOrchestrator>;
  buildPipelineContext: () => any;
  setShouldSkipPriceOrder: (skip: boolean) => void;
  setIsProcessing: (processing: boolean) => void;
  setCurrentError: (error: PaymentPipelineError | null) => void;
  logRBIEvent: (event: AllowedEvent) => void;
}

export async function handlePlaceOrder({
  lastResultRef,
  orchestratorRef,
  setCurrentError,
  setIsProcessing,
  setShouldSkipPriceOrder,
  buildPipelineContext,
  logRBIEvent,
}: HandlePlaceOrderDependencies) {
  const { setRetryInfo, setCurrentState } = usePaymentFlowStore.getState();
  const lastResult = lastResultRef.current;
  const isRetry = !!lastResult?.error?.retryFromStep;

  // add logger before execution
  orchestratorRef.current.setCRMLogger(logRBIEvent);

  // Check context for fresh start
  if (!isRetry) {
    const pipelineContext = buildPipelineContext();
    if (!pipelineContext) {
      setCurrentError({
        code: 'CONTEXT_NOT_READY',
        message: 'Pipeline context is not ready',
        userMessage:
          'Unable to process payment. Please check your payment details and try again. Error Code: (Pipeline Context Readiness)',
      });
      return;
    }
  }

  // Reset state before execution
  setCurrentError(null);
  setIsProcessing(true);
  setRetryInfo(null);
  setCurrentState('idle');

  try {
    // Retry from specific step or start fresh
    const result = isRetry
      ? await orchestratorRef.current.execute(lastResult.context!, {
          skipToStep: lastResult.error!.retryFromStep,
        })
      : await orchestratorRef.current.execute(buildPipelineContext());

    lastResultRef.current = result;

    if (result.success) {
      handleSuccess(setShouldSkipPriceOrder, setRetryInfo, setCurrentError);
      // TODO: do we need warnings for gift cards
      // handle warnings
      // if (result.error) {
      //   handleWarning(result, setCurrentError);
      // } else {
      //   handleSuccess(setShouldSkipPriceOrder, setRetryInfo, setCurrentError);
      // }
    } else {
      handleError(result, setCurrentError, setRetryInfo);
    }
  } catch (error) {
    handleUnexpectedError(error, setCurrentError, setRetryInfo);
  } finally {
    setIsProcessing(false);
  }
}

function handleSuccess(
  setShouldSkipPriceOrder: (skip: boolean) => void,
  setRetryInfo: (info: any) => void,
  setCurrentError: (error: PaymentPipelineError | null) => void
) {
  // Note: resetPaymentMethodStore is now called in the INSERT_SUCCESSFUL effect
  // to prevent the payment method from changing before navigation
  setShouldSkipPriceOrder(true);
  setRetryInfo(null);
  setCurrentError(null);
}

// TODO: do we need warnings for gift cards
// function handleWarning(
//   result: PaymentPipelineResult,
//   setCurrentError: (error: PaymentPipelineError | null) => void
// ) {
//   // Warnings are shown to the user but don't stop the flow
//   setCurrentError(result.error ?? null);
// }

function handleError(
  result: PaymentPipelineResult,
  setCurrentError: (error: PaymentPipelineError | null) => void,
  setRetryInfo: (info: any) => void
) {
  setCurrentError(result.error ?? null);

  if (result.error?.retryFromStep) {
    setRetryInfo({
      canRetry: true,
      retryFromStep: result.error.retryFromStep,
      errorCode: result.error.code,
      errorMessage: result.error.message,
      failedStep: result.failedStep,
    });
  }
}

function handleUnexpectedError(
  error: unknown,
  setCurrentError: (error: PaymentPipelineError | null) => void,
  setRetryInfo: (info: any) => void
) {
  setCurrentError({
    code: 'UNEXPECTED_ERROR',
    message: error instanceof Error ? error.message : 'Unknown error',
    userMessage:
      'An unexpected error occurred. Please try again. Error Code: (Unexpected Orchestrator Error)',
  });
  setRetryInfo(null);
}
